import {useState, useCallback, useEffect, useRef} from 'react'
import {getListCustomShapeTemplate} from '../../../../services/ArtworkTemplateServices'

const DEFAULT_LIMIT = 12
const EMPTY_FILTERS = {}

/**
 * Strips empty-string values so they aren't sent as query params.
 * @param {Record<string, string>} filters
 * @returns {Record<string, string>}
 */
function cleanFilters(filters) {
    const cleaned = {}
    for (const [key, value] of Object.entries(filters)) {
        const trimmed = typeof value === 'string' ? value.trim() : value
        if (trimmed !== '' && trimmed != null) {
            cleaned[key] = trimmed
        }
    }
    return cleaned
}

/**
 * @param {{ page?: number, limit?: number, filters?: Record<string, string> }} [options]
 * @returns {{
 *   items: object[],
 *   total: number,
 *   page: number,
 *   limit: number,
 *   filters: Record<string, string>,
 *   loading: boolean,
 *   error: Error | null,
 *   loadPage: (page: number) => void,
 *   setFilters: (filters: Record<string, string>) => void,
 * }}
 */
export function useShapesTemplateList(options = {}) {
    const {page: initialPage = 1, limit = DEFAULT_LIMIT, filters: initialFilters = EMPTY_FILTERS} = options
    const [page, setPage] = useState(initialPage)
    const [filters, setFiltersState] = useState(initialFilters)
    const [items, setItems] = useState([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const filtersKey = JSON.stringify(filters)

    const loadPage = useCallback((pageNum) => {
        setPage(pageNum)
    }, [])

    /** Update filters and reset to page 1. */
    const setFilters = useCallback((f) => {
        setFiltersState(f)
        setPage(1)
    }, [])

    // Track previous filters so we can detect changes from outside (e.g. initialFilters prop).
    const prevFiltersKeyRef = useRef(filtersKey)
    useEffect(() => {
        if (prevFiltersKeyRef.current !== filtersKey) {
            prevFiltersKeyRef.current = filtersKey
            setPage(1)
        }
    }, [filtersKey])

    useEffect(() => {
        let cancelled = false
        setLoading(true)
        setError(null)

        const params = {page, limit, ...cleanFilters(filters)}

        ;(async () => {
            try {
                const res = await getListCustomShapeTemplate(params)
                if (cancelled) return
                if (!res.success) {
                    setError(new Error('Failed to load shapes'))
                    setItems([])
                    setTotal(0)
                    return
                }
                const data = res.data || {}
                setItems(data.designTemplate || [])
                setTotal(data.total ?? 0)
            } catch (err) {
                if (!cancelled) {
                    setError(err)
                    setItems([])
                    setTotal(0)
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        })()

        return () => {
            cancelled = true
        }
    }, [page, limit, filtersKey])

    return {
        items,
        total,
        page,
        limit,
        filters,
        loading,
        error,
        loadPage,
        setFilters,
    }
}
